import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import {
  FiPlus, FiEdit2, FiTrash2, FiX, FiSave, FiSearch, FiArrowLeft,
  FiCheck, FiEye, FiFileText, FiPrinter, FiXCircle
} from 'react-icons/fi';
import { colors } from '../../theme/colors';
import { getItemsCached, getVendorsCached, getLocationsCached } from '../../utils/masterDataStore';
import { useAuth } from '../../context/AuthContext';
import { missingLocation, reqLabel, LOCATION_REQUIRED_MSG } from '../../utils/locationRequired';
import { formatAmount } from '../../utils/documentNumbering';
import PageLayout from '../../components/PageLayout';
import useDocumentShortcuts from '../../hooks/useDocumentShortcuts';
import usePagePermissions from '../../hooks/usePagePermissions';
import useTableSort from '../../hooks/useTableSort';
import ItemSelect from '../../components/ItemSelect';
import VendorSelect from '../../components/VendorSelect';
import {
  createTransaction, updateTransaction, deleteTransaction,
  getTransaction, updateTransactionStatus
} from '../../utils/transactionHelper';
import useDocListPagination from '../../hooks/useDocListPagination';
import DocListPagination from '../../components/DocListPagination';
import ParentDocLink from '../../components/ParentDocLink';
import { printDocument, loadDocumentFormats } from '../../utils/printTemplates';
import {
  ActionBar, SearchBox, FilterGroup, StatusFilterSelect, TranAddButton, LoadingMessage, TranEmptyState,
  TableContainer, Table, Th, Tr, Td, StatusBadge, ActionGroup, IconBtn, SummaryBar,
  FormHeader, BackButton, FormTitle, FormActions, SaveButton,
  FormCard, SectionTitle, FormGrid, FormGroup, Label, Input, Select, TextArea,
  InfoGrid, InfoItem, NotesText,
  LineItemsHeader, AddLineButton, LineTableContainer, LineTable, LineTh, LineTd, LineInput, RemoveLineBtn,
  TotalsSection, TotalsTable, TotalRow, SortableTh, SortContext
} from '../../components/SharedStyles';
import LineUnitPicker from '../../components/LineUnitPicker';
import { seedLineUnitFromItem, applyUnitChangeToLine } from '../../utils/unitConversion';

const TRAN_TYPE_CODE = 'REQ';

// Footer summary spec — aggregated server-side in paged mode,
// computed from the loaded list in filtered mode. Matches the old footer.
const SUMMARY_SPEC = {
  statuses: ['Draft', 'Submitted', 'Approved', 'Rejected']
};

const EMPTY_LINE = { itemId: '', description: '', quantity: 1, unitOfMeasure: '', unitCode: '', estimatedPrice: 0, preferredVendorId: '', remarks: '', lineTotal: 0 };

const STATUS_COLORS = {
  Draft: colors.warning.main,
  Submitted: colors.info.main,
  Approved: colors.success.main,
  Rejected: colors.error.main,
  Cancelled: colors.error.main,
};

const PurchaseRequisition = () => {
  const location = useLocation();
  const { activeCompany, currentUser, companyUser, activeFinancialYear, activeBranch } = useAuth();
  const { canEdit: canEditPage, canDelete: canDeletePage, canChange } = usePagePermissions('store', 'purchaseRequisition');
  const isAdmin = companyUser?.role === 'admin';
  const showBranchCol = activeBranch && activeBranch.id === 'all';

  const [viewMode, setViewMode] = useState('list');
  const sort = useTableSort('docDate', 'desc');
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');

  // Paginated list (2026-07-16, Firebase cost): 20 docs/page from Firestore.
  // Any search / status filter / custom column sort switches to a one-time
  // full active-year load so filtering behaves exactly as before.
  const listFiltering = searchQuery.trim() !== '' || statusFilter !== 'All'
    || sort.sortField !== 'docDate' || sort.sortDir !== 'desc';
  const {
    docs: records,
    loading,
    refresh: loadRecords,
    ensureDoc,
    applyLocal,
    summary: listSummary,
    pagination
  } = useDocListPagination({
    tranTypeCode: TRAN_TYPE_CODE,
    fullMode: listFiltering,
    summarySpec: SUMMARY_SPEC
  });

  const [editingRecord, setEditingRecord] = useState(null);
  const [viewingRecord, setViewingRecord] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({
    docNumber: '', docDate: new Date().toISOString().split('T')[0],
    requestedBy: '', department: '', requiredDate: '', priority: 'Medium',
    notes: '', status: 'Draft',
    locationId: '', locationName: ''
  });
  const [lineItems, setLineItems] = useState([{ ...EMPTY_LINE }]);
  const [items, setItems] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [locations, setLocations] = useState([]);
  const [printConfig, setPrintConfig] = useState(null);
  // Resolved child PO docs for the requisition currently being viewed.
  // Populated lazily when the view modal opens; ParentDocLink renders each.
  const [linkedChildren, setLinkedChildren] = useState([]);

  // The record list itself loads inside useDocListPagination above
  // (loadRecords = its refresh). The old full-history getTransactions load lived here.

  const loadItems = useCallback(async () => {
    if (!activeCompany?.id) return;
    try {
      // Tier 1: items come from the device-local master cache (0 Firestore reads/open).
      const rows = await getItemsCached(activeCompany.id);
      setItems(rows.filter(i => i.isDeleted !== true).sort((a, b) => (a.itemName || '').localeCompare(b.itemName || '')));
    } catch (error) {
      console.error('Error loading items:', error);
    }
  }, [activeCompany]);

  const loadVendors = useCallback(async () => {
    if (!activeCompany?.id) return;
    try {
      // Tier 1: vendors come from the device-local master cache (0 Firestore reads/open).
      const rows = await getVendorsCached(activeCompany.id);
      setVendors([...rows].sort((a, b) => (a.vendorName || '').localeCompare(b.vendorName || '')));
    } catch (error) {
      console.error('Error loading vendors:', error);
    }
  }, [activeCompany]);

  const loadLocations = useCallback(async () => {
    if (!activeCompany?.id) return;
    try {
      const rows = await getLocationsCached(activeCompany.id);
      setLocations(rows.filter(r => r.isActive === true));
    } catch (error) { console.error('Error loading locations:', error); }
  }, [activeCompany]);

  // NOTE: the record list itself loads inside useDocListPagination — not here.
  useEffect(() => { loadItems(); loadVendors(); loadLocations(); loadDocumentFormats(activeCompany.id).then(setPrintConfig); }, [loadItems, loadVendors, loadLocations]);

  // Open a specific REQ when navigated here from another page (e.g. clicking
  // the "Requisition Ref" link from the Purchase Order view). With pagination
  // the target may not be in the loaded page — ensureDoc fetches it by id.
  useEffect(() => {
    const targetId = location.state?.viewTransactionId;
    if (!targetId) return;
    ensureDoc(targetId).then(rec => {
      if (rec) {
        setViewingRecord(rec);
        setViewMode('view');
        window.history.replaceState({}, document.title);
      }
    });
  }, [location.state, ensureDoc]);

  const filteredRecords = useMemo(() => {
    let filtered = records;
    if (statusFilter !== 'All') filtered = filtered.filter(r => r.status === statusFilter);
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter(r =>
        r.docNumber?.toLowerCase().includes(q) || r.requestedBy?.toLowerCase().includes(q) || r.department?.toLowerCase().includes(q)
      );
    }
    return sort.sortItems(filtered);
  }, [records, statusFilter, searchQuery, sort]);

  const calculateLine = (line) => ({
    ...line,
    unitPrice: line.estimatedPrice || 0,
    lineTotal: Math.round(line.quantity * line.estimatedPrice * 100) / 100
  });

  const totals = useMemo(() => {
    const lines = lineItems.map(calculateLine);
    return { totalEstimate: Math.round(lines.reduce((s, l) => s + l.lineTotal, 0) * 100) / 100 };
  }, [lineItems]);

  const handleHeaderChange = (field, value) => setFormData(prev => ({ ...prev, [field]: value }));

  const handleLineChange = (index, field, value) => {
    setLineItems(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      if (field === 'unitCode' && updated[index].itemId) {
        const item = items.find(i => i.id === updated[index].itemId);
        if (item) {
          const patch = applyUnitChangeToLine(item, value, { priceField: 'purchasePrice' });
          updated[index] = { ...updated[index], unitCode: patch.unitCode, unitOfMeasure: patch.unitOfMeasure, estimatedPrice: patch.unitCost };
        }
      }
      if (field === 'itemId' && value) {
        const item = items.find(i => i.id === value);
        if (item) {
          updated[index].description = item.itemName || '';
          const seeded = seedLineUnitFromItem(item, { priceField: 'purchasePrice', defaultUnitField: 'defaultPurchaseUnit' });
          updated[index] = { ...updated[index], unitCode: seeded.unitCode, unitOfMeasure: seeded.unitOfMeasure, estimatedPrice: seeded.unitCost };
        }
      }
      updated[index] = calculateLine(updated[index]);
      return updated;
    });
  };

  const addLine = () => setLineItems(prev => [...prev, { ...EMPTY_LINE }]);

  const lineItemRefs = useRef([]);
  const pendingFocusRowRef = useRef(null);
  useEffect(() => {
    if (pendingFocusRowRef.current == null) return;
    const rowIdx = pendingFocusRowRef.current;
    pendingFocusRowRef.current = null;
    setTimeout(() => {
      const el = lineItemRefs.current[rowIdx];
      if (el && typeof el.focus === 'function') el.focus();
    }, 50);
  }, [lineItems.length]);

  const handleLastFieldEnter = (e, index) => {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    const isLastRow = index === lineItems.length - 1;
    const line = lineItems[index];
    const hasData = !!line.itemId || (Number(line.quantity) > 0);
    if (!hasData) return;
    if (isLastRow) {
      // eslint-disable-next-line no-alert
      if (window.confirm('Add a new line item?')) {
        pendingFocusRowRef.current = lineItems.length;
        addLine();
      }
    } else {
      const next = lineItemRefs.current[index + 1];
      if (next && typeof next.focus === 'function') next.focus();
    }
  };
  const removeLine = (index) => { if (lineItems.length <= 1) return; setLineItems(prev => prev.filter((_, i) => i !== index)); };

  const resetForm = () => {
    setFormData({ docNumber: '', docDate: new Date().toISOString().split('T')[0], requestedBy: '', department: '', requiredDate: '', priority: 'Medium', notes: '', status: 'Draft', locationId: '', locationName: '' });
    setLineItems([{ ...EMPTY_LINE }]);
    setEditingRecord(null);
  };

  const handleCreate = () => { resetForm(); setViewMode('form'); };
  const handleEdit = (rec) => {
    if (!canChange) { alert('You can add new records but cannot change saved ones.'); return; } // Add-only role (2026-11-04)
    setEditingRecord(rec);
    setFormData({ docNumber: rec.docNumber || '', docDate: rec.docDate || new Date().toISOString().split('T')[0], requestedBy: rec.requestedBy || '', department: rec.department || '', requiredDate: rec.requiredDate || '', priority: rec.priority || 'Medium', notes: rec.notes || '', status: rec.status || 'Draft', locationId: rec.locationId || '', locationName: rec.locationName || '' });
    setLineItems(rec.lines?.length > 0 ? rec.lines.map(l => ({ ...EMPTY_LINE, ...l })) : [{ ...EMPTY_LINE }]);
    setViewMode('form');
  };
  const handleView = (rec) => { setViewingRecord(rec); setViewMode('view'); };

  // Resolve childIds → live transaction docs whenever the view modal opens.
  // Each child carries its own status (PO can be Draft/Approved/Cancelled etc.),
  // which we surface in the view so the buyer sees if the linked PO was
  // cancelled later. We DON'T filter cancelled children out here — the link
  // itself is auditable history, not a live state assertion.
  useEffect(() => {
    if (!viewingRecord || !activeCompany?.id) {
      setLinkedChildren([]);
      return;
    }
    const ids = Array.isArray(viewingRecord.childIds) ? viewingRecord.childIds : [];
    if (ids.length === 0) { setLinkedChildren([]); return; }

    let cancelled = false;
    Promise.all(ids.map(id => getTransaction(activeCompany.id, id).catch(() => null)))
      .then(docs => {
        if (cancelled) return;
        setLinkedChildren(docs.filter(Boolean));
      });
    return () => { cancelled = true; };
  }, [viewingRecord, activeCompany?.id]);

  const canEdit = (rec) => {
    if (rec.status === 'Cancelled') return false;
    if (isAdmin) return true;
    return rec.status === 'Draft';
  };

  const canDelete = (rec) => {
    if (rec.status === 'Cancelled') return false;
    if (isAdmin) return true;
    return rec.status === 'Draft';
  };

  const handleSave = async (saveStatus) => {
    if (!canEditPage) { alert('You do not have permission to save changes.'); return; }
    if (missingLocation(activeCompany, formData.locationId)) { alert(LOCATION_REQUIRED_MSG); return; }
    if (!formData.requestedBy.trim()) { alert('Please enter Requested By'); return; }
    const validLines = lineItems.filter(l => l.itemId && l.quantity > 0);
    if (validLines.length === 0) { alert('Please add at least one item with quantity'); return; }

    // Financial year validation
    if (!activeFinancialYear?.id) {
      alert('Please select an active financial year');
      return;
    }

    setSaving(true);
    try {
      const calculatedLines = validLines.map((l, i) => {
        const item = items.find(it => it.id === l.itemId);
        return { ...calculateLine(l), lineNumber: i + 1, itemCode: item?.itemCode || '', itemName: item?.itemName || l.description || '' };
      });
      const status = saveStatus || 'Draft';
      const data = {
        docDate: formData.docDate,
        requestedBy: formData.requestedBy,
        department: formData.department,
        requiredDate: formData.requiredDate,
        priority: formData.priority,
        locationId: formData.locationId,
        locationName: formData.locationName,
        notes: formData.notes,
        status,
        totalEstimate: totals.totalEstimate,
        lines: calculatedLines,
        financialYearId: activeFinancialYear.id,
        branchId: activeBranch?.id || null,
        branchName: activeBranch?.branchName || null
      };

      let transactionId;
      let newTranData = null;

      if (editingRecord) {
        await updateTransaction(activeCompany.id, editingRecord.id, data, currentUser.uid);
        transactionId = editingRecord.id;
      } else {
        newTranData = await createTransaction(activeCompany.id, { ...data, tranTypeCode: TRAN_TYPE_CODE }, currentUser.uid);
        transactionId = newTranData.id;
      }

      // Optimistic UI update — show the saved record immediately so the Save
      // button releases without waiting on a full collection re-fetch.
      const savedRecord = editingRecord
        ? { ...editingRecord, ...data, id: transactionId, status }
        : { ...newTranData, status };
      applyLocal(prev => editingRecord
        ? prev.map(r => r.id === transactionId ? savedRecord : r)
        : [savedRecord, ...prev]);

      setViewMode('list');
      resetForm();

      // Background refresh — reconciles with server data without blocking UI.
      loadRecords().catch(err => console.error('Background refresh failed:', err));
    } catch (error) {
      console.error('Error saving:', error);
      alert('Error saving: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (rec) => {
    if (!canDeletePage) { alert('You do not have permission to delete.'); return; }
    if (!canDelete(rec)) { alert('This record cannot be deleted'); return; }
    if (!window.confirm(`Delete ${rec.docNumber}?`)) return;
    try {
      await deleteTransaction(activeCompany.id, rec.id);
      await loadRecords();
    } catch (error) { console.error('Error deleting:', error); alert('Error deleting record'); }
  };

  const handleCancel = async (rec) => {
    if (!canEditPage) { alert('You do not have permission to save changes.'); return; }
    if (rec.status === 'Cancelled' || rec.status === 'Rejected') { alert('Cannot cancel this record'); return; }
    if (!window.confirm(`Cancel ${rec.docNumber}?`)) return;
    try {
      await updateTransactionStatus(activeCompany.id, rec.id, 'Cancelled', currentUser.uid);
      await loadRecords();
    } catch (error) { console.error('Error cancelling:', error); alert('Error cancelling record'); }
  };

  const getVendorName = (vendorId) => {
    if (!vendorId) return '-';
    const vendor = vendors.find(v => v.id === vendorId);
    return vendor ? vendor.vendorName : vendorId;
  };

  const handlePrint = (rec) => {
    const data = rec || viewingRecord;
    if (!data) return;
    printDocument('purchaseRequisition', data, activeCompany, printConfig, activeBranch);
  };

  const renderListView = () => (
    <>
      <ActionBar>
        <SearchBox><FiSearch />
          <input type="text" placeholder="Search..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} />
        </SearchBox>
        <FilterGroup>
          <StatusFilterSelect value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
            <option value="All">All Status</option>
            <option value="Draft">Draft</option>
            <option value="Submitted">Submitted</option>
            <option value="Approved">Approved</option>
            <option value="Rejected">Rejected</option>
            <option value="Cancelled">Cancelled</option>
          </StatusFilterSelect>
          {canEditPage && <TranAddButton onClick={handleCreate}><FiPlus /> New Requisition</TranAddButton>}
        </FilterGroup>
      </ActionBar>
      {loading ? <LoadingMessage>Loading...</LoadingMessage> : filteredRecords.length === 0 ? (
        <TranEmptyState><FiFileText size={48} /><h3>No Purchase Requisitions</h3>
          <p>{records.length === 0 ? 'Create your first requisition' : 'No records match filters'}</p>
          {records.length === 0 && canEditPage && <TranAddButton onClick={handleCreate}><FiPlus /> Create Requisition</TranAddButton>}
        </TranEmptyState>
      ) : (
        <TableContainer><SortContext.Provider value={sort.ctxValue}><Table><thead><tr>
          <SortableTh field="docNumber">Doc No</SortableTh><SortableTh field="docDate">Date</SortableTh>{showBranchCol && <SortableTh field="branchName">Branch</SortableTh>}<SortableTh field="requestedBy">Requested By</SortableTh><SortableTh field="department">Department</SortableTh><SortableTh field="priority">Priority</SortableTh>
          <SortableTh field="estimatedTotal" style={{ textAlign: 'right' }}>Estimate</SortableTh><SortableTh field="status" style={{ textAlign: 'center' }}>Status</SortableTh>
          <Th style={{ textAlign: 'center' }}>Actions</Th>
        </tr></thead><tbody>
          {filteredRecords.map(rec => (
            <Tr key={rec.id}>
              <Td><strong>{rec.docNumber}</strong></Td><Td>{rec.docDate}</Td>
              {showBranchCol && <Td>{rec.branchName || '-'}</Td>}
              <Td>{rec.requestedBy || '-'}</Td><Td>{rec.department || '-'}</Td>
              <Td>{rec.priority || '-'}</Td>
              <Td style={{ textAlign: 'right', fontWeight: 500 }}>{activeCompany?.currencyCode || 'PKR'} {formatAmount(rec.totalEstimate)}</Td>
              <Td style={{ textAlign: 'center' }}><StatusBadge $color={STATUS_COLORS[rec.status]}>{rec.status}</StatusBadge></Td>
              <Td style={{ textAlign: 'center' }}><ActionGroup>
                <IconBtn title="View" onClick={() => handleView(rec)}><FiEye /></IconBtn>
                {canChange && canEdit(rec) && <IconBtn title="Edit" onClick={() => handleEdit(rec)}><FiEdit2 /></IconBtn>}
                <IconBtn title="Print" onClick={() => handlePrint(rec)}><FiPrinter /></IconBtn>
                {canDeletePage && canDelete(rec) && <IconBtn title="Delete" $danger onClick={() => handleDelete(rec)}><FiTrash2 /></IconBtn>}
                {canEditPage && rec.status !== 'Cancelled' && rec.status !== 'Rejected' && (
                  <IconBtn title="Cancel" onClick={() => handleCancel(rec)} style={{ color: colors.error.main }}>
                    <FiXCircle />
                  </IconBtn>
                )}
              </ActionGroup></Td>
            </Tr>
          ))}
        </tbody></Table></SortContext.Provider></TableContainer>
      )}
      <DocListPagination {...pagination} />
      <SummaryBar>
        <span>Total: <strong>{listFiltering ? filteredRecords.length : pagination.totalCount}</strong> record(s)</span>
        <span>Draft: <strong>{listSummary.counts.Draft || 0}</strong> | Submitted: <strong>{listSummary.counts.Submitted || 0}</strong> | Approved: <strong>{listSummary.counts.Approved || 0}</strong> | Rejected: <strong>{listSummary.counts.Rejected || 0}</strong></span>
      </SummaryBar>
    </>
  );

  const renderViewMode = () => {
    if (!viewingRecord) return null;
    const rec = viewingRecord;
    return (
      <>
        <FormHeader>
          <BackButton onClick={() => { setViewMode('list'); setViewingRecord(null); }}><FiArrowLeft /> Back to List</BackButton>
          <FormTitle>{rec.docNumber}<StatusBadge $color={STATUS_COLORS[rec.status]} style={{ marginLeft: 12 }}>{rec.status}</StatusBadge></FormTitle>
          <FormActions>
            {canChange && canEdit(rec) && <SaveButton onClick={() => handleEdit(rec)}><FiEdit2 /> Edit</SaveButton>}
            <SaveButton $secondary onClick={() => handlePrint(rec)}><FiPrinter /> Print</SaveButton>
          </FormActions>
        </FormHeader>
        <FormCard>
          <SectionTitle>Requisition Details</SectionTitle>
          <InfoGrid>
            <InfoItem><label>Doc No</label><span>{rec.docNumber}</span></InfoItem>
            <InfoItem><label>Date</label><span>{rec.docDate}</span></InfoItem>
            <InfoItem><label>Requested By</label><span>{rec.requestedBy || '-'}</span></InfoItem>
            <InfoItem><label>Department</label><span>{rec.department || '-'}</span></InfoItem>
            <InfoItem><label>Required Date</label><span>{rec.requiredDate || '-'}</span></InfoItem>
            <InfoItem><label>Priority</label><span>{rec.priority || '-'}</span></InfoItem>
            <InfoItem><label>Location</label><span>{rec.locationName || '-'}</span></InfoItem>
            {linkedChildren.length > 0 && (
              <InfoItem>
                <label>Linked PO{linkedChildren.length > 1 ? 's' : ''}</label>
                <span style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {linkedChildren.map(child => (
                    <ParentDocLink
                      key={child.id}
                      parentId={child.id}
                      parentDocNumber={child.docNumber}
                      parentTranType={child.tranTypeCode || 'PO'}
                    />
                  ))}
                </span>
              </InfoItem>
            )}
          </InfoGrid>
        </FormCard>
        <FormCard>
          <SectionTitle>Items</SectionTitle>
          <TableContainer><Table><thead><tr>
            <Th>#</Th><Th>Description</Th><Th style={{ textAlign: 'right' }}>Qty</Th>
            <Th style={{ textAlign: 'right' }}>Est. Price</Th><Th style={{ textAlign: 'right' }}>Total</Th>
            <Th>Preferred Vendor</Th><Th>Remarks</Th>
          </tr></thead><tbody>
            {(rec.lines || []).map((line, i) => (
              <Tr key={i}><Td>{i + 1}</Td><Td>{line.description}</Td>
                <Td style={{ textAlign: 'right' }}>{line.quantity}{line.unitOfMeasure ? ` ${line.unitOfMeasure}` : ''}</Td>
                <Td style={{ textAlign: 'right' }}>{formatAmount(line.estimatedPrice)}</Td>
                <Td style={{ textAlign: 'right' }}>{formatAmount(line.lineTotal)}</Td>
                <Td>{line.preferredVendorId ? getVendorName(line.preferredVendorId) : '-'}</Td>
                <Td>{line.remarks || '-'}</Td>
              </Tr>
            ))}
          </tbody></Table></TableContainer>
          <TotalsSection><TotalsTable><tbody>
            <TotalRow><td>Total Estimate ({activeCompany?.currencyCode || 'PKR'})</td><td>{formatAmount(rec.totalEstimate)}</td></TotalRow>
          </tbody></TotalsTable></TotalsSection>
        </FormCard>
        {rec.notes && <FormCard><SectionTitle>Notes</SectionTitle><NotesText>{rec.notes}</NotesText></FormCard>}
      </>
    );
  };

  const renderFormView = () => (
    <>
      <FormHeader>
        <BackButton onClick={() => { setViewMode('list'); resetForm(); }}><FiArrowLeft /> Back to List</BackButton>
        <FormTitle>{editingRecord ? `Edit: ${editingRecord.docNumber}` : 'New Purchase Requisition'}</FormTitle>
        <FormActions>
          <SaveButton $secondary onClick={() => { setViewMode('list'); resetForm(); }}><FiX /> Cancel</SaveButton>
          {canEditPage && <SaveButton onClick={() => handleSave('Draft')} disabled={saving}><FiSave /> {saving ? 'Saving...' : 'Save Draft'}</SaveButton>}
          {canEditPage && (formData.status === 'Draft' || !editingRecord) && (
            <SaveButton $approve onClick={() => handleSave('Submitted')} disabled={saving}><FiCheck /> Submit</SaveButton>
          )}
          {canEditPage && isAdmin && editingRecord && formData.status === 'Submitted' && (
            <SaveButton $approve onClick={() => handleSave('Approved')} disabled={saving}><FiCheck /> Approve</SaveButton>
          )}
        </FormActions>
      </FormHeader>
      <FormCard>
        <SectionTitle>Requisition Details</SectionTitle>
        <FormGrid>
          <FormGroup><Label>Doc No</Label><Input value={editingRecord?.docNumber || 'Auto-generated'} disabled /></FormGroup>
          <FormGroup><Label>Date *</Label><Input type="date" value={formData.docDate} onChange={e => handleHeaderChange('docDate', e.target.value)} /></FormGroup>
          <FormGroup><Label>Requested By *</Label><Input value={formData.requestedBy} onChange={e => handleHeaderChange('requestedBy', e.target.value)} placeholder="Name of requester" /></FormGroup>
          <FormGroup><Label>Department</Label><Input value={formData.department} onChange={e => handleHeaderChange('department', e.target.value)} placeholder="Department" /></FormGroup>
          <FormGroup><Label>Required Date</Label><Input type="date" value={formData.requiredDate} onChange={e => handleHeaderChange('requiredDate', e.target.value)} /></FormGroup>
          <FormGroup><Label>Priority *</Label>
            <Select value={formData.priority} onChange={e => handleHeaderChange('priority', e.target.value)}>
              <option value="Low">Low</option><option value="Medium">Medium</option>
              <option value="High">High</option><option value="Urgent">Urgent</option>
            </Select>
          </FormGroup>
          <FormGroup><Label>Status</Label><Input value={formData.status} disabled /></FormGroup>
          <FormGroup>
            <Label>{reqLabel(activeCompany, 'Location')}</Label>
            <Select value={formData.locationId} onChange={e => {
              const loc = locations.find(l => l.id === e.target.value);
              handleHeaderChange('locationId', e.target.value);
              handleHeaderChange('locationName', loc?.locationName || '');
            }}>
              <option value="">-- Select Location --</option>
              {locations.map(loc => (
                <option key={loc.id} value={loc.id}>{loc.locationName}</option>
              ))}
            </Select>
          </FormGroup>
        </FormGrid>
      </FormCard>
      <FormCard>
        <LineItemsHeader>
          <SectionTitle style={{ margin: 0 }}>Items</SectionTitle>
          <AddLineButton onClick={addLine}><FiPlus /> Add Row</AddLineButton>
        </LineItemsHeader>
        <LineTableContainer><LineTable><thead><tr>
          <LineTh style={{ width: 50, minWidth: 50, maxWidth: 50 }}>#</LineTh>
          <LineTh style={{ width: 220, minWidth: 220, maxWidth: 220 }}>Item</LineTh>
          <LineTh style={{ width: 200, minWidth: 200, maxWidth: 200 }}>Description</LineTh>
          <LineTh style={{ width: 120, minWidth: 120, maxWidth: 120 }}>Qty</LineTh>
          <LineTh style={{ width: 140, minWidth: 140, maxWidth: 140 }}>Est. Price</LineTh>
          <LineTh style={{ width: 140, minWidth: 140, maxWidth: 140 }}>Total</LineTh>
          <LineTh style={{ width: 220, minWidth: 220, maxWidth: 220 }}>Preferred Vendor</LineTh>
          <LineTh style={{ width: 150, minWidth: 150, maxWidth: 150 }}>Remarks</LineTh>
          <LineTh style={{ width: 50, minWidth: 50, maxWidth: 50 }}></LineTh>
        </tr></thead><tbody>
          {lineItems.map((line, index) => {
            const calc = calculateLine(line);
            return (
              <tr key={index}>
                <LineTd style={{ textAlign: 'center', color: colors.text.secondary }}>{index + 1}</LineTd>
                <LineTd><ItemSelect ref={(el) => { lineItemRefs.current[index] = el; }} items={items} selectedItemId={line.itemId} onChange={(itemId) => handleLineChange(index, 'itemId', itemId)} /></LineTd>
                <LineTd><LineInput value={line.description} onChange={e => handleLineChange(index, 'description', e.target.value)} placeholder="Description" /></LineTd>
                <LineTd><div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}><LineInput type="number" min="0" value={line.quantity} onChange={e => handleLineChange(index, 'quantity', Number(e.target.value))} style={{ textAlign: 'right' }} /><LineUnitPicker item={items.find(i => i.id === line.itemId)} line={line} onChange={(uc) => handleLineChange(index, 'unitCode', uc)} /></div></LineTd>
                <LineTd><LineInput type="number" min="0" step="0.01" value={line.estimatedPrice} onChange={e => handleLineChange(index, 'estimatedPrice', Number(e.target.value))} style={{ textAlign: 'right' }} /></LineTd>
                <LineTd style={{ textAlign: 'right', fontWeight: 600 }}>{formatAmount(calc.lineTotal)}</LineTd>
                <LineTd><VendorSelect vendors={vendors} selectedVendorId={line.preferredVendorId} onChange={(vendorId) => handleLineChange(index, 'preferredVendorId', vendorId)} /></LineTd>
                <LineTd><LineInput value={line.remarks} onChange={e => handleLineChange(index, 'remarks', e.target.value)} onKeyDown={(e) => handleLastFieldEnter(e, index)} placeholder="Remarks" /></LineTd>
                <LineTd style={{ textAlign: 'center' }}>{lineItems.length > 1 && <RemoveLineBtn onClick={() => removeLine(index)}><FiX /></RemoveLineBtn>}</LineTd>
              </tr>
            );
          })}
        </tbody></LineTable></LineTableContainer>
        <TotalsSection><TotalsTable><tbody>
          <TotalRow><td>Total Estimate ({activeCompany?.currencyCode || 'PKR'})</td><td>{formatAmount(totals.totalEstimate)}</td></TotalRow>
        </tbody></TotalsTable></TotalsSection>
      </FormCard>
      <FormCard>
        <SectionTitle>Notes</SectionTitle>
        <FormGrid><FormGroup $span2><TextArea value={formData.notes} onChange={e => handleHeaderChange('notes', e.target.value)} placeholder="Additional notes..." rows={3} /></FormGroup></FormGrid>
      </FormCard>
    </>
  );

  useDocumentShortcuts({
    onNew: canEditPage ? handleCreate : undefined,
    onSave: canEditPage ? () => handleSave('Submitted') : undefined,
    viewMode,
    saving
  });

  return (
    <PageLayout pageTitle="Purchase Requisition" breadcrumb="Store / Purchase Requisition">
      {viewMode === 'list' && renderListView()}
      {viewMode === 'form' && renderFormView()}
      {viewMode === 'view' && renderViewMode()}
    </PageLayout>
  );
};

export default PurchaseRequisition;
